/**
 * Client for the Servicekatalog page (ADR 0060 phase 5): the catalogue,
 * server-side pricing, saved quotes, approvals, conversion and the customer
 * lookup. Every call goes through `authHeaders` so it works in dev mode
 * (X-Dev-User) and with MSAL (Bearer).
 */
import { acquireToken, USE_REAL_AUTH } from "../auth/msal";

const API_BASE = import.meta.env.VITE_API_BASE ?? "";

async function authHeaders(devUser?: string): Promise<Record<string, string>> {
  const h: Record<string, string> = { "Content-Type": "application/json" };
  if (USE_REAL_AUTH) {
    const acquired = await acquireToken();
    if (acquired) h.Authorization = `Bearer ${acquired.token}`;
  } else if (devUser) {
    h["X-Dev-User"] = devUser;
  }
  return h;
}

export class ServiceCatalogError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "ServiceCatalogError";
  }
}

async function call<T>(
  path: string,
  devUser: string | undefined,
  init: RequestInit = {},
): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { ...(await authHeaders(devUser)), ...(init.headers ?? {}) },
  });
  if (!res.ok) {
    let detail = "";
    try {
      const body = await res.json();
      detail =
        typeof body?.detail === "string"
          ? body.detail
          : JSON.stringify(body?.detail ?? body);
    } catch {
      detail = await res.text().catch(() => "");
    }
    throw new ServiceCatalogError(detail || `HTTP ${res.status}`, res.status);
  }
  return (await res.json()) as T;
}

export interface CatalogTier {
  from_qty: number;
  to_qty: number | null;
  unit_price: number;
}
export interface CatalogProduct {
  product_id: string;
  sort_order: number;
  category: string;
  name: string;
  unit: string;
  period: "monthly" | "annual" | "onetime";
  rule_type: "unit" | "tier" | "group" | "formula";
  currency: "DKK" | "USD" | null;
  unit_price: number | null;
  group_name: string | null;
  group_subsequent_price: number | null;
  formula_expr: string | null;
  formula_params: Record<string, number>;
  /** The prototype's "solve for" hint on a formula; carried, not edited. */
  formula_solve_for?: string | null;
  tiers: CatalogTier[];
  note: string;
  source_ref: string;
}
export interface CatalogQuestion {
  question_id: string;
  category: string;
  label: string;
  kind: "text" | "number" | "choice";
  options: string[];
}
/** Where the defaults come from: the config file, or an owner's save on
 * the page (after which the file no longer overwrites them). */
export interface CatalogSource {
  source: "file" | "editor";
  edited_by?: string;
  edited_at?: string;
}
export interface CatalogView {
  products: CatalogProduct[];
  questions: CatalogQuestion[];
  usd_to_dkk: number;
  source?: CatalogSource;
}
/** One product as the editor holds it — the same fields as CatalogProduct
 * minus sort_order (the list's order is the order) and with formula
 * params as a record. */
export type EditableProduct = Omit<CatalogProduct, "sort_order">;
export interface CatalogDiff {
  added: string[];
  removed: string[];
  changed: string[];
  products: number;
  usd_to_dkk: { before: number; after: number };
}

export interface LineInput {
  product_id: string;
  quantity: number;
  unit_price?: number;
}
export interface PricedLine {
  product_id: string;
  name: string;
  category: string;
  unit: string;
  period: string;
  quantity: number;
  rule_type: string;
  currency: string | null;
  unit_price: number | null;
  list_unit_price: number | null;
  override: boolean;
  amount: number | null;
  amount_dkk: number | null;
  status:
    | "ok"
    | "idle"
    | "no-currency"
    | "no-price"
    | "no-tier"
    | "bad-formula";
  detail: string;
  note: string;
}
export interface PricePayload {
  lines: PricedLine[];
  totals_dkk: {
    monthly: number;
    annual: number;
    onetime: number;
    year_one: number;
  };
  by_period_and_currency: Record<string, Record<string, number>>;
  usd_to_dkk: number;
  flags: string[];
  unknown_product_ids: string[];
  service_questions: {
    category: string;
    label: string;
    kind: string;
    options: string[];
  }[];
}

export type Party = "customer" | "seller" | "ops";
export interface Approval {
  by: string;
  at: string;
  note: string;
}
export interface Quote {
  quote_id: string;
  title: string;
  status: "draft" | "sent" | "approved" | "contract" | "cancelled";
  customer_name: string;
  customer_cvr: string;
  customer_address: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  notes: string;
  answers: Record<string, string>;
  lines: PricedLine[];
  flags: string[];
  approvals: Partial<Record<Party, Approval>>;
  missing_approvals: Party[];
  monthly_dkk: number | null;
  annual_dkk: number | null;
  onetime_dkk: number | null;
  year_one_dkk: number | null;
  usd_to_dkk: number | null;
  contract_id: string | null;
  created_by_email: string;
  created_at: string;
  updated_at: string;
  priced_at?: string;
  /** The caller may delete it: creator or owner for a draft, sent or
   * cancelled quote; an admin for any. Set by the server. */
  can_delete?: boolean;
}
export type QuoteSummary = Pick<
  Quote,
  | "quote_id"
  | "title"
  | "status"
  | "customer_name"
  | "customer_cvr"
  | "contact_name"
  | "monthly_dkk"
  | "annual_dkk"
  | "onetime_dkk"
  | "year_one_dkk"
  | "approvals"
  | "missing_approvals"
  | "contract_id"
  | "created_by_email"
  | "created_at"
  | "updated_at"
  | "can_delete"
>;
export interface Contract {
  contract_id: string;
  quote_id: string;
  title: string;
  status: string;
  customer_name: string;
  start_date: string | null;
  end_date: string | null;
  term_months: number | null;
  notice_months: number | null;
  monthly_dkk: number | null;
  year_one_dkk: number | null;
}
export interface Customer {
  name: string;
  cvr: string;
  address: string;
  source: string;
  reference: string;
}
export interface Me {
  assistant: { slug: string; name: string };
  is_admin: boolean;
  is_owner: boolean;
  can: Record<string, boolean>;
  /** Owner, co-owner or admin: may save the catalogue's defaults. */
  can_edit_catalog: boolean;
  email: string;
}

export interface SaveQuoteInput {
  quote_id?: string;
  customer: {
    name: string;
    cvr?: string;
    address?: string;
    contact_name?: string;
    contact_email?: string;
    contact_phone?: string;
  };
  lines: LineInput[];
  title?: string;
  notes?: string;
  answers?: Record<string, string>;
  usd_to_dkk?: number;
}

export const serviceCatalogApi = {
  me: (devUser?: string) => call<Me>("/api/service-catalog/me", devUser),
  catalog: (devUser?: string) =>
    call<CatalogView>("/api/service-catalog/catalog", devUser),
  price: (lines: LineInput[], usdToDkk: number | undefined, devUser?: string) =>
    call<PricePayload>("/api/service-catalog/price", devUser, {
      method: "POST",
      body: JSON.stringify({ lines, usd_to_dkk: usdToDkk }),
    }),
  listQuotes: (q: string, status: string, devUser?: string) =>
    call<{ quotes: QuoteSummary[] }>(
      `/api/service-catalog/quotes?q=${encodeURIComponent(q)}&status_filter=${encodeURIComponent(status)}`,
      devUser,
    ),
  getQuote: (id: string, devUser?: string) =>
    call<{ quote: Quote }>(
      `/api/service-catalog/quotes/${encodeURIComponent(id)}`,
      devUser,
    ),
  saveQuote: (input: SaveQuoteInput, devUser?: string) =>
    call<{ quote: Quote }>("/api/service-catalog/quotes", devUser, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  setStatus: (id: string, status: string, note: string, devUser?: string) =>
    call<{ quote: Quote }>(
      `/api/service-catalog/quotes/${encodeURIComponent(id)}/status`,
      devUser,
      {
        method: "POST",
        body: JSON.stringify({ status, note }),
      },
    ),
  approve: (
    id: string,
    party: Party,
    approvedBy: string,
    note: string,
    devUser?: string,
  ) =>
    call<{ quote: Quote }>(
      `/api/service-catalog/quotes/${encodeURIComponent(id)}/approvals`,
      devUser,
      {
        method: "POST",
        body: JSON.stringify({ party, approved_by: approvedBy, note }),
      },
    ),
  convert: (
    id: string,
    body: {
      start_date: string;
      term_months?: number;
      notice_months?: number;
      notes?: string;
    },
    devUser?: string,
  ) =>
    call<{ contract: Contract }>(
      `/api/service-catalog/quotes/${encodeURIComponent(id)}/convert`,
      devUser,
      { method: "POST", body: JSON.stringify(body) },
    ),
  customers: (q: string, devUser?: string) =>
    call<{ customers: Customer[]; sources: string[] }>(
      `/api/service-catalog/customers?q=${encodeURIComponent(q)}`,
      devUser,
    ),
  /** Delete a quote. Its number stays taken; it is never issued again. */
  deleteQuote: (id: string, devUser?: string) =>
    call<{ ok: boolean; quote_id: string; contract_id: string | null }>(
      `/api/service-catalog/quotes/${encodeURIComponent(id)}`,
      devUser,
      { method: "DELETE" },
    ),
  /** Owner/admin: save the catalogue's defaults. The list's order is the
   * sort order; validation errors come back as one Danish sentence. */
  updateCatalog: (
    products: EditableProduct[],
    usdToDkk: number,
    devUser?: string,
  ) =>
    call<CatalogView & { diff: CatalogDiff }>(
      "/api/service-catalog/catalog",
      devUser,
      {
        method: "PUT",
        body: JSON.stringify({ products, usd_to_dkk: usdToDkk }),
      },
    ),
  /** Owner/admin: the catalogue as the prototype's export JSON. */
  exportCatalog: (devUser?: string) =>
    download("/api/service-catalog/catalog/export", devUser),
  /** The offer as a Word file, built server-side from the saved record. */
  downloadQuoteDocument: (id: string, devUser?: string) =>
    download(
      `/api/service-catalog/quotes/${encodeURIComponent(id)}/document`,
      devUser,
    ),
  /** Contracts' service agreement template, filled from a saved quote (draft). */
  downloadQuoteAgreement: (id: string, devUser?: string) =>
    download(`/api/service-catalog/quotes/${encodeURIComponent(id)}/agreement`, devUser),
  /** Contracts' service agreement template, filled from the contract record. */
  downloadContractAgreement: (id: string, devUser?: string) =>
    download(`/api/service-catalog/contracts/${encodeURIComponent(id)}/agreement`, devUser),
  /** The contract draft as a Word file, from the contract record. */
  downloadContractDocument: (id: string, devUser?: string) =>
    download(
      `/api/service-catalog/contracts/${encodeURIComponent(id)}/document`,
      devUser,
    ),
};

/** Authenticated download to a local file — a plain <a href> cannot carry
 * the bearer. The filename comes from the server's Content-Disposition. */
async function download(path: string, devUser?: string): Promise<string> {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: await authHeaders(devUser),
  });
  if (!res.ok) {
    let detail = "";
    try {
      const body = await res.json();
      detail = typeof body?.detail === "string" ? body.detail : "";
    } catch {
      /* no JSON body */
    }
    throw new ServiceCatalogError(detail || `HTTP ${res.status}`, res.status);
  }
  const disposition = res.headers.get("Content-Disposition") ?? "";
  const m = /filename="([^"]+)"/.exec(disposition);
  const filename = m?.[1] ?? "dokument.docx";
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return filename;
}
